import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import { css } from "styled-components"
import BreakpointImage from "../../atoms/breakpoint-image"
import { Block, Spacer } from "../../atoms/layout-primitives"
import Text from "../../atoms/text"
import { Alignments } from "../../molecules/icon-title"
import IconTitleCard from "../../molecules/icon-title-card"
import { T } from "../../styles/typography"

const BottomSectionIconGroup = () => {
  const query = useStaticQuery(graphql`
    query {
      permissionsIcon: file(
        relativePath: { eq: "4_COMPANY/Security/icons/icon 04.svg" }
      ) {
        publicURL
      }
      twoFactorIcon: file(
        relativePath: { eq: "4_COMPANY/Security/icons/icon 05.svg" }
      ) {
        publicURL
      }
      ssoIcon: file(
        relativePath: { eq: "4_COMPANY/Security/icons/icon 06.svg" }
      ) {
        publicURL
      }
      hostingIcon: file(
        relativePath: { eq: "4_COMPANY/Security/icons/icon 07.svg" }
      ) {
        publicURL
      }
      encryptionIcon: file(
        relativePath: { eq: "4_COMPANY/Security/icons/icon 08.svg" }
      ) {
        publicURL
      }
      cloudIcon: file(
        relativePath: { eq: "4_COMPANY/Security/icons/icon 09.svg" }
      ) {
        publicURL
      }
      infrastructureDesktop: file(
        relativePath: {
          eq: "4_COMPANY/Security/secure infrastructure - desk.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1200) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      infrastructureImage: file(
        relativePath: { eq: "4_COMPANY/Security/secure infrastructure.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1200) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)
  return (
    <>
      <Block
        display={{ sm: "grid", md: "grid", lg: "grid" }}
        col={{ sm: "1 / -1", md: "3 / -3", lg: "1 / -1" }}
        variants={[
          css`
            grid-template-columns: 1fr;
            grid-column: 1 / -1;
            width: 100%;
          `,
          null,
          css`
            grid-template-columns: 1fr 1fr 1fr;
            grid-gap: 48px;
            padding-left: 1.11%;
            padding-right: 1.11%;
          `,
        ]}
      >
        <IconTitleCard
          icon={
            <img src={query.permissionsIcon.publicURL} alt="Permissions Icon" />
          }
          title="Permissions Manager"
          description="Mappedin’s Permissions system allows you to easily set different map access for employees across your organization."
          titleVariants={[
            css`
              margin-top: 48px;
              ${T.sm.h4`500`}
            `,
            css`
              ${T.md.h4`500`}
            `,
            css`
              margin-top: 96px;
              ${T.lg.h4`500`}
            `,
          ]}
          descriptionVariants={{ sm: { textAlign: "center" } }}
          titleAlignmentVariants={[
            Alignments["row-center"],
            Alignments["row-center"],
            Alignments["column-centered"],
          ]}
          iconVariants={{
            sm: { width: "24px", height: "24px" },
            lg: { width: "48px", height: "48px" },
          }}
        />
        <IconTitleCard
          icon={
            <img
              src={query.twoFactorIcon.publicURL}
              alt="Two Factor Authorization Icon"
            />
          }
          title="Two Factor Authentication"
          description="Mappedin’s two factor authentication ensures the utmost security for our customers maps and associated data."
          titleVariants={[
            css`
              margin-top: 48px;
              ${T.sm.h4`500`}
              justify-content: center;
              text-align: center;
            `,
            css`
              ${T.md.h4`500`}
            `,
            css`
              margin-top: 96px;
              ${T.lg.h4`500`}
            `,
          ]}
          descriptionVariants={{ sm: { textAlign: "center" } }}
          titleAlignmentVariants={[
            Alignments["row-center"],
            Alignments["row-center"],
            Alignments["column-centered"],
          ]}
          iconVariants={{
            sm: { width: "24px", height: "24px" },
            lg: { width: "48px", height: "48px" },
          }}
        />
        <IconTitleCard
          icon={<img src={query.ssoIcon.publicURL} alt="Single Sign On Icon" />}
          title="Single Sign On"
          description="SSO allows our customers to enforce their own company-specific controls for accessing the Mappedin CMS."
          titleVariants={[
            css`
              margin-top: 48px;
              ${T.sm.h4`500`}
            `,
            css`
              ${T.md.h4`500`}
            `,
            css`
              margin-top: 96px;
              ${T.lg.h4`500`}
            `,
          ]}
          descriptionVariants={{ sm: { textAlign: "center" } }}
          titleAlignmentVariants={[
            Alignments["row-center"],
            Alignments["row-center"],
            Alignments["column-centered"],
          ]}
          iconVariants={{
            sm: { width: "24px", height: "24px" },
            lg: { width: "48px", height: "48px" },
          }}
        />
      </Block>

      {/* Secure Infrastructure section */}

      <Spacer height={{ sm: "64px", md: "96px", lg: "128px" }} />
      <Block col={{ sm: "1 / -1", md: "3 / -3", lg: "1 / 7" }}>
        <BreakpointImage
          imageVariants={{
            sm: query.infrastructureImage.childImageSharp.fluid,
            md: query.infrastructureImage.childImageSharp.fluid,
            lg: query.infrastructureDesktop.childImageSharp.fluid,
          }}
        />
      </Block>
      <Spacer
        height={{ sm: "32px", md: "48px", lg: "0px" }}
        display={{ lg: "none" }}
      />
      <Block
        col={{ sm: "1 / -1", md: "2 / -2", lg: "8 / -1" }}
        variants={[
          css`
            text-align: center;
          `,
          null,
          css`
            text-align: left;
            align-self: center;
          `,
        ]}
      >
        <Text
          variants={[
            css`
              ${T.md.h2`500`}
              letter-spacing: 0.05em;
              color: var(--slate);
            `,
            css`
              letter-spacing: 0.08em;
            `,
            css`
              ${T.lg.h2`500`}
            `,
          ]}
        >
          Secure Infrastructure
        </Text>
        <Text
          variants={[
            css`
              ${T.sm.body`400`}
              margin-top: 24px;
              color: var(--body-text-grey);
            `,
            css`
              ${T.md.body`400`}
            `,
            css`
              ${T.lg.body`400`}
            `,
          ]}
        >
          Mappedin has implemented the highest security standards possible to
          ensure your maps and associated data are secure at all times.
        </Text>
      </Block>

      <Block
        display={{ sm: "grid", md: "grid", lg: "grid" }}
        col={{ sm: "1 / -1", md: "3 / -3", lg: "1 / -1" }}
        variants={[
          css`
            grid-template-columns: 1fr;
            grid-column: 1 / -1;
            width: 100%;
          `,
          null,
          css`
            grid-template-columns: 1fr 1fr 1fr;
            grid-gap: 48px;
            padding-left: 1.11%;
            padding-right: 1.11%;
          `,
        ]}
      >
        <IconTitleCard
          icon={
            <img src={query.hostingIcon.publicURL} alt="Data Hosting Icon" />
          }
          title="Data Hosting"
          description="Mappedin hosts data in Amazon Web Services (AWS) data centers in the United States and the European Union. We also leverage Azure for certain file uploads to our system. Both AWS and Azure are certified as compliant with ISO 27001 and have received a SOC 2 (Type 2) report."
          titleVariants={[
            css`
              margin-top: 48px;
              ${T.sm.h4`500`}
            `,
            css`
              ${T.md.h4`500`}
            `,
            css`
              margin-top: 96px;
              ${T.lg.h4`500`}
            `,
          ]}
          descriptionVariants={{ sm: { textAlign: "center" } }}
          titleAlignmentVariants={[
            Alignments["row-center"],
            Alignments["row-center"],
            Alignments["column-centered"],
          ]}
          iconVariants={{
            sm: { width: "24px", height: "24px" },
            lg: { width: "48px", height: "48px" },
          }}
        />
        <IconTitleCard
          icon={
            <img src={query.encryptionIcon.publicURL} alt="Encryption Icon" />
          }
          title="Data Encryption"
          description="Mappedin encrypts all data in transit and at rest. Data transfer is protected using the industry-standard TLS 1.2 protocol, while data at rest in AWS is encrypted using AES-256 server-side encryption. Mappedin uses AWS Key Management Services for database encryption and secure key management."
          titleVariants={[
            css`
              margin-top: 48px;
              ${T.sm.h4`500`}
            `,
            css`
              ${T.md.h4`500`}
            `,
            css`
              margin-top: 96px;
              ${T.lg.h4`500`}
            `,
          ]}
          descriptionVariants={{ sm: { textAlign: "center" } }}
          titleAlignmentVariants={[
            Alignments["row-center"],
            Alignments["row-center"],
            Alignments["column-centered"],
          ]}
          iconVariants={{
            sm: { width: "24px", height: "24px" },
            lg: { width: "48px", height: "48px" },
          }}
        />
        <IconTitleCard
          icon={
            <img src={query.cloudIcon.publicURL} alt="Cloud Platform Icon" />
          }
          title="Cloud Platform"
          description="All components that process map data operate in Mappedin’s secure cloud platform. Mappedin’s servers and network ports are behind load balancers with an intrusion detection system in place. Additionally, Mappedin reviews all data sub-processors to conduct due diligence on policies and procedures."
          titleVariants={[
            css`
              margin-top: 48px;
              ${T.sm.h4`500`}
            `,
            css`
              ${T.md.h4`500`}
            `,
            css`
              margin-top: 96px;
              ${T.lg.h4`500`}
            `,
          ]}
          descriptionVariants={{ sm: { textAlign: "center" } }}
          titleAlignmentVariants={[
            Alignments["row-center"],
            Alignments["row-center"],
            Alignments["column-centered"],
          ]}
          iconVariants={{
            sm: { width: "24px", height: "24px" },
            lg: { width: "48px", height: "48px" },
          }}
        />
      </Block>
    </>
  )
}

export default BottomSectionIconGroup
